import type { AdType } from '@/modules/adv/types'

//#region GA events types
export type EventGA = 'custom_event' | 'interface_event' | 'view_search_results'
type InterfaceGAEvents =
    | 'interface_click'
    | 'interface_show'
    | 'interface_hide'
    | 'interface_rotate'
    | 'interface_scroll'
    | 'pwa_native_install_prompt_shown'
    | 'pwa_portal_install_prompt_shown'
    | 'pwa_portal_install_game_prompt_shown'
    | 'pwa_portal_install_game_prompt_shown_toast'
    | 'pwa_portal_install_prompt_shown_toast'

type CTREvents = 'click' | 'impression'

export type LogSource = 'ad-sdk' | 'wrap' | 'default'

export const GAMEPLAY_EVENT_THRESHOLDS_MS = {
    game: 0,
    game_30s: 30_000,
    game_60s: 60_000,
} as const

export const ADJUST_LOG_TOKENS: Partial<Record<AvailablePGEvents, string>> = {
    game: 'yr6hfz',
    game_30s: 'f4nmqi',
    playgame_5min: 'sesdsp',
}

export const ADJUST_TOKENS: Partial<Record<AvailablePGEvents, string>> = {
    ...ADJUST_LOG_TOKENS,
    purchase_successful: '43vpyb',
}

/* FOR GA events please don't use following names https://support.google.com/analytics/answer/9234069?hl=ru
  as it can lead to collecting data hell
*/
export type AvailableEvent =
    | 'ad_autoplay_check'
    | 'ad_blocker_detect_attempt'
    | 'ad_blocker_offer'
    | 'ad_blocker_offer_fail'
    | 'ad_blocker_offer_skip'
    | 'ad_blocker_offer_success'
    | 'ad_bot_detect_attempt'
    | 'ad_close'
    | 'ad_empty'
    | 'ad_error'
    | 'ad_h5_reload'
    | 'ad_hb_auction'
    | 'ad_hb_auction_expired'
    | 'ad_hb_auction_timeout'
    | 'ad_hb_bidder_request'
    | 'ad_hb_bidder_response'
    | 'ad_hb_fallback_bid_used'
    | 'ad_hb_invalid_format_bid'
    | 'ad_hb_render_failed'
    | 'ad_hb_user_ids'
    | 'ad_rendered'
    | 'ad_sdk_external_gpt_detected'
    | 'ad_sdk_unknown_domain'
    | 'ad_request'
    | 'ad_request_attempt_failed'
    | 'tmp_ad_preoad_used'
    | 'ad_service_ready'
    | 'ad_show'
    | 'ad_show_high_value'
    | 'ad_show_midroll'
    | 'ad_show_not_called_after_ready'
    | 'ad_skipped'
    | 'ad_timeout'
    | 'ad_unexpected_behavior'
    | 'ad_gpt_diagnostic'
    | 'ad_waterfall_attempt'
    | 'claim'
    | 'consent_change'
    | 'consent_init'
    | 'do-task'
    | 'export-init-click'
    | 'game'
    | 'game_15s'
    | 'game_30s'
    | 'game_60s'
    | 'game_bridge_loaded'
    | 'game_preloader_timeout'
    | 'game_preview'
    | 'game_ready'
    | 'in_game_ad_not_requested'
    | 'invite-friend'
    | 'login'
    | 'ms_store_url_opened'
    | 'native_app_promo_rating_show'
    | 'open_purchase_window'
    | 'page_lcp'
    | 'page_cls'
    | 'page_inp'
    | 'page_ttfb'
    | 'play'
    | 'playgame_10min'
    | 'playgame_3min'
    | 'playgame_5min'
    | 'portal_session_start'
    | 'preroll_end'
    | 'pwa_native_install_accepted'
    | 'share'
    | 'sign-in'
    | 'sign-out'
    | 'suggested_game_from_unavailable_game_click'
    | 'timespent_ltv'
    | 'view_search_results'
    | 'vue_message'
    | CTREvents
    | InterfaceGAEvents

type CommonEventGA = {
    event: EventGA
    eventName: AvailableEvent
    clid?: 'direct' | string
    widget_id: string
    route_name?: string
    experiments?: string
}

type InterfaceEventGA = CommonEventGA & {
    interface_target: string
    interface_path: string
}

type SearchEventGA = CommonEventGA & {
    search_term: string
    search_result_count: number
}

export type CustomEventForGa = CommonEventGA & {
    action: string
    // TODO separate ad events
    ad_placement: string
    game_provider: string
    game_categories: string[]
    ad_unit: string
    instream_duration: number | null
    watched_duration: number | null
    waterfall_index: number | null
    error_message: string
    amount_in_game_currency: number | null
}

export type EventForGa = CustomEventForGa | InterfaceEventGA | SearchEventGA

//#endregion

export type VoteEvent = 'reaction_like' | 'reaction_dislike' | 'reaction_unset_like' | 'reaction_unset_dislike'
type AuthEvent = 'auth_success' | 'auth_failed'
type AuthPopupEvent = 'auth_popup_opened' | 'auth_popup_closed' | 'auth_popup_auto_opened'
type PaymentPopupEvent =
    | 'payment_modal_opened'
    | 'purchase_successful'
    | 'purchase_fail'
    | 'new_subscription_after_in_game_purchase'
type PushSubscriptionEvent = 'push_notification_granted' | 'push_notification_denied'
type GooglePlayModalEvent =
    | 'google_play_modal_opened'
    | 'google_play_modal_closed'
    | 'google_play_install_clicked'
    | 'google_play_app_opened'
type GooglePlayGameEvent = 'google_play_game_shown' | 'google_play_game_clicked'
type LoopEvent = 'loop_session_start' | 'loop_play'
export type PopupEvent = 'popup_show' | 'popup_click' | 'popup_close'

export type AvailablePGEvents =
    | AvailableEvent
    | CustomPGEvents
    | LoopEvent
    | VoteEvent
    | AuthEvent
    | AuthPopupEvent
    | PaymentPopupEvent
    | PushSubscriptionEvent
    | GooglePlayModalEvent
    | GooglePlayGameEvent
    | PopupEvent
export type CustomPGEvents =
    | 'try-escape-webview'
    | 'escaped-webview'
    | 'game_opened'
    | 'game_autoplay'
    | 'ping'
    | 'navigation'
    | 'portal_session_end'
    | 'homepage_5s'
// 'adsdk_event' marks a site-owned event pushed through the Ads SDK `window.pgEvents` queue
type EventsType = EventGA | 'user_time' | 'adsdk_event'

// Atención!
// Before changing this type, you have to add columns to clickhouse, otherwise these fileds will get lost
type AdditionalEventProps = {
    dimension1: string
    dimension2: string
    dimension3: string
}

export type LoggerEvent = Partial<AdditionalEventProps> & {
    event: EventsType
    eventName: AvailablePGEvents
    // Honoured by the Ads SDK logger only (default 'partner-sdk'); the portal always logs its route name
    pageName?: string
    action?: string
    adPlacement?: AdType
    adUnit?: string
    adProvider?: string
    adHbBidder?: string
    adHbCpmUsd?: number
    adHbRequestId?: string
    errorMessage?: string
    instreamDuration?: number
    watchedDuration?: number
    waterfallIndex?: number
    amountInGameCurrency?: number
    externaId?: string
    source?: LogSource
}

export type LogEventOptions = { flushImmediately?: boolean }

export type StartRumEvent = (init: {
    operationName: string
}) => (arg: {
    status: 'success' | 'error' | 'empty'
    dimension1?: string
    dimension2?: string
    dimension3?: string
    dimension4?: string
}) => void
